import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  BOOKING_RULE_BOUNDS,
  bookingRuleError,
  toRuleNumber,
  type BookingRuleField,
} from '../../lib/appointments/rules';
import type { BookingRules } from '../../types/appointments';

// The calendar's booking rules — GHL's booking-rules screen, in our own form
// components.
//
// ⚠️ THE VALUES ARE HELD AS STRINGS by the parent so an emptied box stays empty
// while it is being retyped instead of snapping to 0, and `toRuleNumber` reads a
// blank as NaN — never as 0, because `Number('')` is 0 and that is the one value
// the two caps must never store. The lead bot's caps editor learned this the same
// way.
//
// ⚠️ Validation is the SHARED `bookingRuleError`, so the save button and the
// server cannot disagree about what a legal setting is.

export type RuleDraft = Record<BookingRuleField, string>;

export function ruleDraftFrom(rules: BookingRules): RuleDraft {
  return {
    meetingIntervalMinutes: String(rules.meetingIntervalMinutes),
    meetingDurationMinutes: String(rules.meetingDurationMinutes),
    minimumNoticeMinutes: String(rules.minimumNoticeMinutes),
    dateRangeDays: String(rules.dateRangeDays),
    preBufferMinutes: String(rules.preBufferMinutes),
    postBufferMinutes: String(rules.postBufferMinutes),
    maxPerDay: String(rules.maxPerDay),
    maxPerSlot: String(rules.maxPerSlot),
  };
}

export function ruleDraftErrors(draft: RuleDraft): Partial<Record<BookingRuleField, string>> {
  const out: Partial<Record<BookingRuleField, string>> = {};
  for (const field of Object.keys(BOOKING_RULE_BOUNDS) as BookingRuleField[]) {
    const message = bookingRuleError(toRuleNumber(draft[field]), field);
    if (message) out[field] = message;
  }
  return out;
}

export function rulesFromDraft(draft: RuleDraft): BookingRules {
  return {
    meetingIntervalMinutes: toRuleNumber(draft.meetingIntervalMinutes),
    meetingDurationMinutes: toRuleNumber(draft.meetingDurationMinutes),
    minimumNoticeMinutes: toRuleNumber(draft.minimumNoticeMinutes),
    dateRangeDays: toRuleNumber(draft.dateRangeDays),
    preBufferMinutes: toRuleNumber(draft.preBufferMinutes),
    postBufferMinutes: toRuleNumber(draft.postBufferMinutes),
    maxPerDay: toRuleNumber(draft.maxPerDay),
    maxPerSlot: toRuleNumber(draft.maxPerSlot),
  };
}

const ORDER: Array<{ field: BookingRuleField; unit: string }> = [
  { field: 'meetingIntervalMinutes', unit: 'minutes' },
  { field: 'meetingDurationMinutes', unit: 'minutes' },
  { field: 'minimumNoticeMinutes', unit: 'minutes' },
  { field: 'dateRangeDays', unit: 'days' },
  { field: 'preBufferMinutes', unit: 'minutes' },
  { field: 'postBufferMinutes', unit: 'minutes' },
  { field: 'maxPerDay', unit: 'bookings' },
  { field: 'maxPerSlot', unit: 'bookings' },
];

export function BookingRulesEditor({
  value,
  onChange,
}: {
  value: RuleDraft;
  onChange: (next: RuleDraft) => void;
}) {
  const errors = ruleDraftErrors(value);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {ORDER.map(({ field, unit }) => {
          const bound = BOOKING_RULE_BOUNDS[field];
          return (
            <div key={field}>
              <Label htmlFor={`rule-${field}`}>{bound.label}</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id={`rule-${field}`}
                  type="number"
                  className="w-28"
                  value={value[field]}
                  onChange={(e) => onChange({ ...value, [field]: e.target.value })}
                />
                <span className="text-xs text-muted-foreground">{unit}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{bound.hint}</p>
              {errors[field] && (
                <p className="mt-1 text-xs text-rose-700">{errors[field]}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* ⚠️ THE TWO MISREADINGS THIS BOX EXISTS TO PREVENT, both of which lead
          somebody to set the wrong number and then be surprised by a real
          booking. Interval-vs-duration is invisible while they are equal, and
          maxPerDay looks like a limit on the salesperson's whole diary. */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <p className="mb-1.5">
          <span className="font-medium text-foreground">Interval and duration are different.</span>{' '}
          The interval is the gap between the times we offer; the duration is how long the
          appointment runs. Set a 15-minute interval with a 60-minute duration and you will offer
          9:00, 9:15, 9:30 — each an hour long.
        </p>
        <p className="mb-1.5">
          <span className="font-medium text-foreground">
            The daily and per-slot limits only count bookings made through Kyck
          </span>{' '}
          — and they count across the whole calendar, not per person. Anything a salesperson puts
          in their own calendar takes up their time, so we will not book over it, but it does not
          count towards these limits. We can see <em>when</em> someone is busy, not what for.
        </p>
        <p>
          <span className="font-medium text-foreground">The buffers only affect what we offer.</span>{' '}
          The calendar event itself is exactly the duration long, so a salesperson booking
          something manually can still land inside a buffer — their calendar does not know ours
          exists.
        </p>
      </div>
    </div>
  );
}
